import { useEffect, useState } from "react";
import "./ConnPage.css";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert } from "../components/Card";
import { Field, Input, Select } from "../components/Field";
import { Chip } from "../components/Status";
import { FormSplit, PageHead, StatCard } from "../components/Page";
import { hole, rich, tx } from "../i18n";
import { DbixSection } from "./ConnDbix";

/* ══ 연결 설정 ══
   채택 시안(안 A · 한 화면 폼, Figma node 3:3). term-mops 「AI 도우미」 구조를 따르되
   **오류를 셋으로 갈랐다** — 사람이 고쳐야 할 데가 셋 다 다르기 때문이다.

   **이 화면은 진짜로 통신한다.** 다만 브라우저가 ola-mops 를 직접 부르지는 않는다:
   b3x-in API 가 대신 부른다. 그래야 ⓐ 토큰이 화면까지 안 내려오고 ⓑ ola-mops 에
   CORS 를 뚫을 일이 없다. 화면이 받는 것은 「걸려 있나(tokenSet)」뿐이다. */

const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

type Saved = { baseUrl: string; tokenSet: boolean; model: string };
type TestKind = "ok" | "no-models" | "unauthorized" | "not-found" | "http-error" | "unreachable" | "no-address";
type TestResult = { kind: TestKind; status?: number; ms?: number; models: string[]; detail?: string };

/** 결과 배너. **넷 다 문구 · 아이콘 · 색 셋을 함께 쓴다** — 색만으로 가르지 않는다.
 *  그리고 「허용된 모델 없음」은 **오류가 아니다**: 붙는 데까지는 다 된 상태라
 *  빨갛게 칠하면 사람이 주소부터 다시 본다. */
const BANNER: Record<TestKind, { tone: "success" | "warning" | "error" | "info"; head: string; body: string }> = {
  ok: { tone: "success", head: tx("conn.result.ok.head"), body: tx("conn.result.ok.body") },
  "no-models": { tone: "warning", head: tx("conn.result.noModels.head"), body: tx("conn.result.noModels.body") },
  unauthorized: { tone: "error", head: tx("conn.result.unauthorized.head"), body: tx("conn.result.unauthorized.body") },
  "not-found": { tone: "error", head: tx("conn.result.notFound.head"), body: tx("conn.result.notFound.body") },
  "http-error": { tone: "error", head: tx("conn.result.httpError.head"), body: tx("conn.result.httpError.body") },
  unreachable: { tone: "error", head: tx("conn.result.unreachable.head"), body: tx("conn.result.unreachable.body") },
  "no-address": { tone: "info", head: tx("conn.result.noAddress.head"), body: tx("conn.result.noAddress.body") },
};

export function ConnPage() {
  const [saved, setSaved] = useState<Saved | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [token, setToken] = useState("");
  const [model, setModel] = useState("");
  const [models, setModels] = useState<string[]>([]);
  const [result, setResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/admin/connection`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((s: Saved) => {
        setSaved(s);
        setBaseUrl(s.baseUrl);
        setModel(s.model);
      })
      .catch((e: Error) => setLoadErr(e.message));
  }, []);

  /* 저장하지 않은 것이 있나. **저장줄은 그때만 뜬다**(term-mops 선례) — 늘 떠 있으면
     눌러야 할 것처럼 보이고, 안 뜨면 고친 것이 남아 있는 줄 모른다. */
  const dirty =
    saved !== null && (baseUrl !== saved.baseUrl || model !== saved.model || token.length > 0);

  async function test() {
    setTesting(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/admin/connection/test`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        /* **저장 없이 지금 칸에 적힌 값으로 시험한다**(시안 확정). 저장해야 시험이 되면,
           안 되는 값을 저장한 뒤라야 안 되는 줄 알게 된다. 토큰을 안 적었으면 서버가
           저장된 토큰을 쓴다 — 화면은 그 값을 모르기 때문이다. */
        body: JSON.stringify({ baseUrl, token: token || undefined }),
      });
      const data: TestResult = await r.json();
      setResult(data);
      setModels(data.models ?? []);
      // 목록이 왔는데 고른 모델이 그 안에 없으면 첫 번째로 옮긴다.
      if (data.models?.length && !data.models.includes(model)) setModel(data.models[0]);
    } catch (e) {
      setResult({ kind: "unreachable", models: [], detail: (e as Error).message });
    } finally {
      setTesting(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/connection`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        /* 토큰은 **적었을 때만** 보낸다. 안 보내면 서버가 그대로 두는데, 그래야
           주소만 고치러 와서 저장했을 때 토큰이 날아가지 않는다. */
        body: JSON.stringify({ baseUrl, model, token: token ? token : undefined }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const s: Saved = await r.json();
      setSaved(s);
      setBaseUrl(s.baseUrl);
      setModel(s.model);
      setToken(""); // 저장한 토큰은 화면에 안 남긴다.
    } catch (e) {
      setResult({ kind: "unreachable", models: [], detail: tx("settings.vendors.saveFailed", { err: (e as Error).message }) });
    } finally {
      setSaving(false);
    }
  }

  const b = result ? BANNER[result.kind] : null;
  /* 보조 열 — 저장된 값과 이번 시험. 못 읽었으면 「모름」이다(빈 값으로 그리면 「안 걸렸다」로 읽힌다). */
  const known = saved !== null;
  const unknown = tx(loadErr ? "conn.stat.unknown" : "common.state.loading");
  const statRows = [
    { label: tx("conn.stat.address"), value: known ? saved.baseUrl || tx("conn.stat.empty") : unknown },
    { label: tx("chat.tokens"), value: known ? tx(saved.tokenSet ? "conn.stat.set" : "conn.stat.empty") : unknown },
    { label: tx("conn.model"), value: known ? saved.model || tx("conn.stat.modelEmpty") : unknown },
    { label: tx("conn.stat.test"), value: b ? b.head : tx("conn.stat.notRun") },
  ];

  return (
    <AppShell route="/conn">
      <PageHead
        title={tx("nav.conn")}
        lead={
          <>
            <p>{rich(tx("conn.lead1", hole("b")), { b: <b>{tx("conn.lead1Bold")}</b> })}</p>
            <p>{rich(tx("conn.lead2", hole("b")), { b: <b>{tx("conn.lead2Bold")}</b> })}</p>
          </>
        }
      />

      {loadErr ? <Alert tone="error">{tx("conn.loadFailed", { err: loadErr })}</Alert> : null}

      <FormSplit aside={<StatCard rows={statRows} />}>
      <div className="conn__form">
        <Field label={tx("conn.address")} hint={tx("conn.addressHint")}>
          {(id) => (
            <Input
              id={id}
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="http://ola-mops.local:30088"
              inputMode="url"
            />
          )}
        </Field>

        <Field
          label={tx("conn.token")}
          hint={tx(saved?.tokenSet ? "conn.tokenHintSet" : "conn.tokenHintNone")}
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={tx(saved?.tokenSet ? "conn.tokenPhSet" : "conn.tokenPhNone")}
              autoComplete="off"
            />
          )}
        </Field>

        <div className="conn__modelrow">
          <Field
            label={tx("conn.model")}
            hint={tx(models.length ? "conn.modelHintList" : "conn.modelHintNone")}
          >
            {(id) =>
              models.length ? (
                <Select id={id} value={model} onChange={(e) => setModel(e.target.value)}>
                  {models.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              ) : (
                /* 목록이 없을 때도 저장된 값은 보여야 한다 — 빈 드롭다운을 두면
                   무엇이 걸려 있었는지가 화면에서 사라진다. */
                <Input id={id} value={model} onChange={(e) => setModel(e.target.value)} placeholder={tx("conn.modelPh")} />
              )
            }
          </Field>
          <Button variant="primary" size="lg" onClick={test} loading={testing}>
            {tx("conn.test")}
          </Button>
        </div>
      </div>

      {b && result ? (
        <div className="conn__banner">
          <Alert tone={b.tone}>
            <b>{b.head}</b>
            {result.status ? ` (${result.status})` : ""} — {b.body}
            {result.kind === "ok" ? ` ${tx("conn.okDetail", { n: result.models.length, ms: result.ms ?? "?" })}` : ""}
            {result.detail ? ` [${result.detail}]` : ""}
          </Alert>
        </div>
      ) : null}
      </FormSplit>

      {/* 저장줄은 **고친 것이 있을 때만** 뜬다. 값을 고치고 한참 내려가도 손이 닿도록
          화면 아래에 붙어 있는다(term-mops 선례). */}
      {dirty ? (
        <div className="conn__save">
          <span>
            {tx("settings.vendors.unsaved")}
            {token ? <Chip tone="warning">{tx("settings.vendors.changed", { track: tx("chat.tokens") })}</Chip> : null}
            {saved && baseUrl !== saved.baseUrl ? <Chip tone="warning">{tx("conn.addressChanged")}</Chip> : null}
            {saved && model !== saved.model ? <Chip tone="warning">{tx("settings.vendors.changed", { track: tx("conn.model") })}</Chip> : null}
          </span>
          <span className="conn__savebtns">
            <Button
              onClick={() => {
                if (!saved) return;
                setBaseUrl(saved.baseUrl);
                setModel(saved.model);
                setToken("");
              }}
            >
              {tx("settings.vendors.revert")}
            </Button>
            <Button variant="primary" onClick={save} loading={saving}>
              {tx("common.action.save")}
            </Button>
          </span>
        </div>
      ) : null}

      {/* dbix — 사용이력(채택 시안 A안: 연결 설정 안의 한 절). */}
      <DbixSection />
    </AppShell>
  );
}
